/* eslint complexity: ["error", 40] */

import React, {Fragment} from 'react';
import PropTypes from 'prop-types';
import {Button, Fade, Container, Row, Col, Form} from 'react-bootstrap';
import {PayPalButton} from 'react-paypal-button-v2';
import {Redirect} from 'react-router-dom';
import CommonMetaTag from './common-meta-tag';
import {Formik} from 'formik';
import {FilePond, registerPlugin} from 'react-filepond';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import {FaImage} from 'react-icons/fa';
import Configuration from '../config';
import {imageTypes} from '../constants';
import 'filepond/dist/filepond.min.css';
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css";
import {useTranslation} from 'react-i18next';
import parking from '../images/parking_icon.svg';
import lockerRooms from '../images/locker_room_icon.svg';
import showers from '../images/shower_icon.svg';
import restaurant from '../images/ristorante.svg';
import bar from '../images/bar_icon.svg';
import shop from '../images/negozio.svg';
import picnic from '../images/picnic_area_icon.svg';
import tournaments from '../images/tournaments_icon.svg';
import petFriendly from '../images/pets_icon.svg';
import accessibility from '../images/disabled_icon.svg';
import relax from '../images/relax_icon.svg';
import AuthMethods from './auth-methods';
registerPlugin(FilePondPluginImagePreview);

const PartnerProfile = ({
  profile, save, token, removeFile, topUpWalletSuccess, showNotClosePaypalMessage, ownedRtvChannels, deleteRtvChannelImageFile, showInvalidAmountMessage
}) => {
  const {t} = useTranslation();
  if (profile === null) {
    return (
      <Redirect to="/login"/>
    );
  }

  const imageLogo = (profile.storeImage || []).map(f => {
    return {
      source: f,
      options: {
        type: 'local'
      }
    };
  });

  const dedicatedPageHeroImage = (profile.rtvImage || []).map(f => {
    return {
      source: f,
      options: {
        type: 'local'
      }
    };
  });

  const imagesNames = profile.imagesNames.map(f => {
    return {
      source: f,
      options: {
        type: 'local'
      }
    };
  });

  const imagePartnerVideo = (profile.videoImage || []).map(f => {
    return {
      source: f,
      options: {
        type: 'local'
      }
    };
  });

  const isValidTopupAmount = (amount) => {
    return amount === undefined || amount === "" || (amount >= 5 && amount <= 9999);
  };

  const facilityOptions = [
    {name: 'parking', label: t('PARK'), icon: parking},
    {name: 'lockerRooms', label: t('LOCKER_ROOMS'), icon: lockerRooms},
    {name: 'showers', label: t('SHOWERS'), icon: showers},
    {name: 'restaurant', label: t('RESTAURANT_PIZZERIA'), icon: restaurant},
    {name: 'bar', label: t('BAR_PUB'), icon: bar},
    {name: 'shop', label: t('SHOP_RENTAL'), icon: shop},
    {name: 'picnic', label: t('PARTY_PICNIC'), icon: picnic},
    {name: 'tournaments', label: t('TOURNAMENTS'), icon: tournaments},
    {name: 'petFriendly', label: t('PET_FRIENDLY'), icon: petFriendly},
    {name: 'accessibility', label: t('DISABLED_ACCESS'), icon: accessibility},
    {name: 'relaxArea', label: t('RELAX_AREA'), icon: relax}
  ];

  const renderImageGuide = ({description, height, title, width}) => (
    <div className="partner-profile-image-guide">
      <div className="partner-profile-image-guide__copy">
        <Form.Label>{title}</Form.Label>
        <Form.Text className="text-muted">{description}</Form.Text>
      </div>
      <div className="partner-profile-image-guide__size">
        <FaImage aria-hidden="true"/>
        <span>{t('PARTNER_IMAGE_RECOMMENDED_SIZE')}</span>
        <strong>{width} x {height} px</strong>
      </div>
    </div>
  );

  return (
    <Fragment>
      <CommonMetaTag/>
      <Fade in appear style={{transition: 'opacity 0.30s linear', marginTop: 100}}>
        <Container className="partner-profile-page">
          <Row className="justify-content-center">
            <Col xs={12} lg={10}>
              <div className="partner-profile-hero">
                <span>{profile.isSpecialist ? t('PARTNER_PROFILE_SPECIALIST') : t('PARTNER_PROFILE')}</span>
                <h4>{profile.name || profile.businessName}</h4>
                <p>{t('PARTNER_PROFILE_INTRO')}</p>
              </div>
              <div className="partner-profile-card partner-profile-summary-card">
                <div className="partner-profile-section-title">
                  <span>{t('COMPANY_DATA')}</span>
                  <h5>{t('ACCOUNT_INFORMATION')}</h5>
                  <p>{t('ACCOUNT_INFORMATION_HELP')}</p>
                </div>
                <div className="partner-profile-summary-grid">
                  <div><span>{t('BUSINESS_NAME')}</span><strong>{profile.businessName}</strong></div>
                  <div><span>{t('REGISTERED_OFFICE')}</span><strong>{profile.registeredOffice}</strong></div>
                  <div><span>{t('VAT_NUMBER')}</span><strong>{profile.iva}</strong></div>
                  <div><span>{t('SDI_PEC')}</span><strong>{profile.sdiPec}</strong></div>
                  <div><span>{t('EMAIL')}</span><strong>{profile.user.email}</strong></div>
                </div>
              </div>
              <div className="partner-profile-card">
                <div className="partner-profile-section-title">
                  <span>{t('PARTNER_MEDIA_SECTION_LABEL')}</span>
                  <h5>{t('PARTNER_MEDIA_SECTION_TITLE')}</h5>
                  <p>{t('PARTNER_MEDIA_SECTION_HELP')}</p>
                </div>
              {renderImageGuide({
                description: t('PARTNER_IMAGE_CLUB_LOGO_HELP'),
                height: 512,
                title: t('PARTNER_IMAGE_CLUB_LOGO_TITLE'),
                width: 512
              })}
              <FilePond
                files={imageLogo}
                server={
                  {
                    url: `${Configuration.apiEndPoint}/`,
                    process: {
                      url: './images',
                      ondata: formData => {
                        formData.append('image_type', imageTypes.logo);
                        return formData;
                      }
                    },
                    revert: './images',
                    restore: './images',
                    fetch: './images',
                    load: `./images/${profile.uid}?load=`,
                    headers: {Authorization: `Bearer ${token}`},
                    remove: (source, load) => {
                      removeFile(source);
                      load();
                    }
                  }
                }
              />
              {renderImageGuide({
                description: t('PARTNER_IMAGE_DEDICATED_HERO_HELP'),
                height: 1440,
                title: t('PARTNER_IMAGE_DEDICATED_HERO_TITLE'),
                width: 2560
              })}
              <FilePond
                files={dedicatedPageHeroImage}
                maxFiles={1}
                server={
                  {
                    url: `${Configuration.apiEndPoint}/`,
                    process: {
                      url: './images',
                      ondata: formData => {
                        formData.append('image_type', imageTypes.rtv);
                        return formData;
                      }
                    },
                    revert: './images',
                    restore: './images',
                    fetch: './images',
                    load: `./images/${profile.uid}?load=`,
                    headers: {Authorization: `Bearer ${token}`},
                    remove: (source, load) => {
                      removeFile(source);
                      load();
                    }
                  }
                }
              />
              {renderImageGuide({
                description: t('PARTNER_IMAGE_SHOWCASE_HELP'),
                height: 1440,
                title: t('PARTNER_IMAGE_SHOWCASE_TITLE'),
                width: 2560
              })}
              <FilePond
                allowMultiple
                files={imagesNames}
                server={
                  {
                    url: `${Configuration.apiEndPoint}/`,
                    process: {
                      url: './images',
                      ondata: formData => {
                        formData.append('image_type', imageTypes.partner);
                        return formData;
                      }
                    },
                    revert: './images',
                    restore: './images',
                    fetch: './images',
                    load: `./images/${profile.uid}?load=`,
                    headers: {Authorization: `Bearer ${token}`},
                    remove: (source, load) => {
                      removeFile(source);
                      load();
                    }
                  }
                }
                maxFiles={3}
              />
              </div>

              {
                profile.rtvChannels.length > 0 ?
                  <div className="partner-profile-card">
                    <div className="partner-profile-section-title">
                      <span>ReplayerTV</span>
                      <h5>{t('PARTNER_RTV_BACKGROUNDS_SECTION_TITLE')}</h5>
                      <p>{t('PARTNER_RTV_BACKGROUNDS_SECTION_HELP')}</p>
                    </div>
                  </div> :
                  null
              }
              
              {
                profile.rtvChannels.map(c => (
                  <div className="partner-profile-card partner-profile-subcard" key={`rtv-bg-${c.id}`}>
                    {renderImageGuide({
                      description: t('PARTNER_IMAGE_RTV_BACKGROUND_HELP', {channel: c.name}),
                      height: 1440,
                      title: t('PARTNER_IMAGE_RTV_BACKGROUND_TITLE', {channel: c.name}),
                      width: 2560
                    })}
                    <FilePond
                      allowMultiple
                      files={(c.imagesNames || []).map(f => {
                        return {
                          source: f,
                          options: {
                            type: 'local'
                          }
                        };
                      })}
                      server={
                        {
                          url: `${Configuration.apiEndPoint}/partners/`,
                          process: {
                            url: `./rtv_channels/${c.id}/images`,
                            ondata: formData => {
                              formData.append('image_type', imageTypes.rtv);
                              return formData;
                            }
                          },
                          revert: `./rtv_channels/${c.id}/images`,
                          restore: `./rtv_channels/${c.id}/images`,
                          fetch: `./rtv_channels/${c.id}/images`,
                          load: `./rtv_channels/${c.id}/images?load=`,
                          headers: {Authorization: `Bearer ${token}`},
                          remove: (source, load) => {
                            deleteRtvChannelImageFile(source, c.id);
                            load();
                          }
                        }
                      }
                      maxFiles={3}
                    />
                  </div>
                ))
              }              
              
              {
                profile.rtvChannels.length > 0 ?
                  <div className="partner-profile-card">
                    <div className="partner-profile-section-title">
                      <span>ReplayerTV</span>
                      <h5>{t('PARTNER_RTV_LOGOS_SECTION_TITLE')}</h5>
                      <p>{t('PARTNER_RTV_LOGOS_SECTION_HELP')}</p>
                    </div>
                  </div> :
                  null
              }

              {
                profile.rtvChannels.map(c => (
                  <div className="partner-profile-card partner-profile-subcard" key={`rtv-logo-${c.id}`}>
                    {renderImageGuide({
                      description: t('PARTNER_IMAGE_RTV_LOGO_HELP', {channel: c.name}),
                      height: 512,
                      title: t('PARTNER_IMAGE_RTV_LOGO_TITLE', {channel: c.name}),
                      width: 512
                    })}
                    <FilePond
                      allowMultiple
                      files={(c.logoNames || []).map(f => {
                        return {
                          source: f,
                          options: {
                            type: 'local'
                          }
                        };
                      })}
                      server={
                        {
                          url: `${Configuration.apiEndPoint}/partners/`,
                          process: {
                            url: `./rtv_channels/${c.id}/images`,
                            ondata: formData => {
                              formData.append('image_type', imageTypes.logo);
                              return formData;
                            }
                          },
                          revert: `./rtv_channels/${c.id}/images`,
                          restore: `./rtv_channels/${c.id}/images`,
                          fetch: `./rtv_channels/${c.id}/images`,
                          load: `./rtv_channels/${c.id}/images?load=`,
                          headers: {Authorization: `Bearer ${token}`},
                          remove: (source, load) => {
                            deleteRtvChannelImageFile(source, c.id);
                            load();
                          }
                        }
                      }
                      maxFiles={3}
                    />
                  </div>
                ))
              }
              <div className="partner-profile-card">
                <div className="partner-profile-section-title">
                  <span>{t('PARTNER_VIDEO_IMAGE_SECTION_LABEL')}</span>
                  <h5>{t('PARTNER_VIDEO_IMAGE_SECTION_TITLE')}</h5>
                  <p>{t('PARTNER_VIDEO_IMAGE_SECTION_HELP')}</p>
                </div>
              {renderImageGuide({
                description: t('PARTNER_IMAGE_VIDEO_COVER_HELP'),
                height: 1440,
                title: t('PARTNER_IMAGE_VIDEO_COVER_TITLE'),
                width: 2560
              })}
              <FilePond
                files={imagePartnerVideo}
                server={
                  {
                    url: `${Configuration.apiEndPoint}/`,
                    process: {
                      url: './images',
                      ondata: formData => {
                        formData.append('image_type', imageTypes.partnerVideoCover);
                        return formData;
                      }
                    },
                    revert: './images',
                    restore: './images',
                    fetch: './images',
                    load: `./images/${profile.uid}?load=`,
                    headers: {Authorization: `Bearer ${token}`},
                    remove: (source, load) => {
                      removeFile(source);
                      load();
                    }
                  }
                }
              />
              </div>
              <Formik
                enableReinitialize
                initialValues={Object.assign({}, profile)}
                onSubmit={save}
              >
                {({
                  handleSubmit,
                  handleChange,
                  setFieldValue,
                  values
                }) => {
                  return (
	                    <Container fluid className="partner-profile-form-container">
	                      <Form onSubmit={handleSubmit}>
                          <div className="partner-profile-card">
                            <div className="partner-profile-section-title">
                              <span>{t('PUBLIC_PROFILE')}</span>
                              <h5>{t('VISIBLE_REPLAYER_DATA')}</h5>
                              <p>{t('VISIBLE_REPLAYER_DATA_HELP')}</p>
                            </div>
	                        <Form.Group>
	                          <Form.Label>{t('NAME')}*</Form.Label>
                          <Form.Control required name="name" type="text" placeholder={t('NAME')} maxLength={50} value={values.name || ''} onChange={handleChange}/>
                          <Form.Text className="text-muted">
                            {t('NAME')}
                          </Form.Text>
                        </Form.Group>
                        <Form.Group>
                          <Form.Label>{t('ADDRESS')}*</Form.Label>
                          <Form.Control required name="address" type="text" placeholder={t('ADDRESS')} maxLength={255} value={values.address || ''} onChange={handleChange}/>
                          <Form.Text className="text-muted">
                            {t('ADDRESS')}
                          </Form.Text>
                        </Form.Group>
                        <Form.Row>
                          <Col>
                            <Form.Group>
                              <Form.Label>{t('PROVINCE')}*</Form.Label>
                              <Form.Control required name="province" type="text" placeholder={t('PROVINCE')} maxLength={2} value={values.province || ''} onChange={handleChange}/>
                              <Form.Text className="text-muted">
                                {t('PROVINCE')}
                              </Form.Text>
                            </Form.Group>
                          </Col>
                          <Col>
                            <Form.Group>
                              <Form.Label>{t('ZIP_CODE')}*</Form.Label>
                              <Form.Control required name="CAP" type="text" placeholder={t('ZIP_CODE')} maxLength={5} value={values.CAP || ''} onChange={handleChange}/>
                              <Form.Text className="text-muted">
                                {t('ZIP_CODE')}
                              </Form.Text>
                            </Form.Group>
                          </Col>
                          <Col>
                            <Form.Group>
                              <Form.Label>{t('CITY')}*</Form.Label>
                              <Form.Control required name="city" type="text" placeholder={t('CITY')} maxLength={255} value={values.city || ''} onChange={handleChange}/>
                              <Form.Text className="text-muted">
                                {t('CITY')}
                              </Form.Text>
                            </Form.Group>
                          </Col>
                        </Form.Row>
                        <Form.Group>
                          <Form.Label>{t('TELEPHONE')}*</Form.Label>
                          <Form.Control required name="phoneNumber" type="tel" placeholder={t('TELEPHONE')} maxLength={13} value={values.phoneNumber || ''} onChange={handleChange}/>
                          <Form.Text className="text-muted">
                            {t('TELEPHONE')}
                          </Form.Text>
                        </Form.Group>
                        <Form.Group>
                          <Form.Label>{t('DESCRIPTION')}*</Form.Label>
                          <Form.Control required name="description" type="text" placeholder={t('DESCRIPTION')} maxLength={255} value={values.description || ''} onChange={handleChange}/>
                          <Form.Text className="text-muted">
                            {t('DESCRIPTION')}
                          </Form.Text>
                        </Form.Group>
                        <Form.Group>
                          <Form.Label>{t('DESCRIPTION_ACCURATE')}*</Form.Label>
                          <Form.Control required name="extDescription" as="textarea" rows="7" placeholder={t('DESCRIPTION_ACCURATE')} maxLength={1000} value={values.extDescription || ''} onChange={handleChange}/>
                          <Form.Text className="text-muted">
                            {t('DESCRIPTION_ACCURATE')}
                          </Form.Text>
                        </Form.Group>
                        <Form.Group>
                          <Form.Label>{t('SPORTS_CENTER_HOURS')}</Form.Label>
                          <Form.Control name="openingHour.sportsCenter" as="textarea" rows="7" placeholder={t('SPORTS_CENTER_HOURS')} maxLength={1000} value={values.openingHour ? values.openingHour.sportsCenter : ''} onChange={handleChange}/>
                          <Form.Text className="text-muted">
                            {t('SPORTS_CENTER_HOURS')}
                          </Form.Text>
                        </Form.Group>
                        <Form.Group>
                          <Form.Label>{t('SECRETARIAT_HOURS')}</Form.Label>
                          <Form.Control name="openingHour.secretary" as="textarea" rows="7" placeholder={t('SECRETARIAT_HOURS')} maxLength={1000} value={values.openingHour ? values.openingHour.secretary : ''} onChange={handleChange}/>
                          <Form.Text className="text-muted">
                            {t('SECRETARIAT_HOURS')}
                          </Form.Text>
                        </Form.Group>
                        <Form.Group>
                          <Form.Label>{t('VISIBILITY_CALENDAR')}*</Form.Label>
                          <Form.Control required name="defaultVisibilityCalendar" type="number" min="0" max="90" step="1" placeholder={t('VISIBILITY_CALENDAR')} value={values.defaultVisibilityCalendar >= 0 ? values.defaultVisibilityCalendar : ''} onChange={handleChange}/>
                          <Form.Text className="text-muted">
                            {t('VISIBILITY_CALENDAR')}
                          </Form.Text>
                        </Form.Group>
                        <Form.Group>
                          <Form.Label>{t('SERVICES')}</Form.Label>
                          <Form.Text className="text-muted">
                            {t('SELECT_SERVICES_FOR_CLUB_PAGE')}
                          </Form.Text>
                          <div className="partner-profile-facilities-grid">
                            {facilityOptions.map(facility => (
                              <Form.Check
                                key={facility.name}
                                id={`partner-profile-facility-${facility.name}`}
                                name={`facilities.${facility.name}`}
                                type="checkbox"
                                label={(
                                  <span className="partner-profile-facility-label">
                                    <img src={facility.icon} alt="" />
                                    <span>{facility.label}</span>
                                  </span>
                                )}
                                checked={values.facilities && values.facilities[facility.name]}
                                onChange={() => setFieldValue(`facilities.${facility.name}`, !(values.facilities && values.facilities[facility.name]))}
                              />
                            ))}
                          </div>
                        </Form.Group>
                        <Form.Group>
                          <Form.Label>{t('WEBSITE')}</Form.Label>
                          <Form.Control name="website" type="url" placeholder={t('WEBSITE')} maxLength={253} value={values.website || ''} onChange={handleChange}/>
                          <Form.Text className="text-muted">
                            {t('WEBSITE')}
                          </Form.Text>
                        </Form.Group>
                        <Form.Group>
                          <Form.Label>{t('FACEBOOK')}</Form.Label>
                          <Form.Control name="facebook" type="url" placeholder={t('FACEBOOK')} maxLength={2048} value={values.facebook || ''} onChange={handleChange}/>
                          <Form.Text className="text-muted">
                            {t('FACEBOOK')}
                          </Form.Text>
                        </Form.Group>
                        <Form.Group>
                          <Form.Label>{t('INSTAGRAM')}</Form.Label>
                          <Form.Control name="instagram" type="url" placeholder={t('INSTAGRAM')} maxLength={2048} value={values.instagram || ''} onChange={handleChange}/>
                          <Form.Text className="text-muted">
                            {t('INSTAGRAM')}
                          </Form.Text>
                        </Form.Group>
                        <Form.Group>
                          <Form.Label>{t('YOUTUBE')}</Form.Label>
                          <Form.Control name="youtube" type="url" placeholder={t('YOUTUBE')} maxLength={2048} value={values.youtube || ''} onChange={handleChange}/>
                          <Form.Text className="text-muted">
                            {t('YOUTUBE')}
                          </Form.Text>
                        </Form.Group>
                        <Form.Group>
                          <Form.Label>{t('READY_TO_GO')}</Form.Label>
                            <Col className="partner-profile-visibility-options d-flex flex-column">
                              <Form.Check required name="visible" type="radio" label={t('PARTNER_DRAFT')} checked={!values.visible} onChange={() => setFieldValue('visible', false)}/>
                              <Form.Check required name="visible" type="radio" label={t('PARTNER_PUBLIC')} checked={values.visible} onChange={() => setFieldValue('visible', true)}/>
	                            </Col>
	                        </Form.Group>
	                        <Button type="submit" className="partner-profile-save-button">{t('SAVE')}</Button>
                          </div>
                          <div className="partner-profile-card partner-profile-wallet-card">
                            <div className="partner-profile-section-title">
                              <span>{t('CREDIT')}</span>
                              <h5>{t('MY_CREDIT')}</h5>
                              <p>{t('PARTNER_CREDIT_HELP')}</p>
                            </div>
	                        <Form.Group>
	                          <Form.Label>{t('CREDIT_VALUE', {value: values.user.wallet})}</Form.Label>
                        </Form.Group>
                        <Form.Group>
                          <Form.Label>{t('ACCOUNT_TOPUP')} (€)</Form.Label>
                          <Form.Control type="number" name="topupAmount" value={values.topupAmount || null} step={1} min={5} max={9999} onChange={handleChange}/>
                          <Form.Text className="text-muted">{t('MIN_MAX',{min: '5,00 €', max: '9999,00 €'})}</Form.Text>
                        </Form.Group>
                        <PayPalButton
                          options={{
                            clientId: Configuration.paypalClientId,
                            currency: 'EUR'
                          }}
                          createOrder={(data, actions) => {
                            if (!isValidTopupAmount(values.topupAmount)) {
                              showInvalidAmountMessage();
                              return Promise.reject(new Error(t('OPERATION_ERROR')));
                            }
                            return actions.order.create({
                              purchase_units: [{
                                amount: {
                                  value: values && values.topupAmount ? values.topupAmount : 5
                                }
                              }],
                              application_context: {
                                locale: 'it-IT',
                                shipping_preference: 'NO_SHIPPING'
                              }
                            }).then(orderID => {
                              // Send order created to the server
                              return fetch(`${Configuration.apiEndPoint}/paypal/order_created`, {
                                method: 'POST',
                                headers: {
                                  'content-type': 'application/json',
                                  Accept: 'application/json',
                                  Authorization: `Bearer ${token}`
                                },
                                mode: 'cors',
                                body: JSON.stringify({
                                  orderID
                                })
                              }).then(res => {
                                // TODO: Mostrare errore in caso di fallimento di questa richiesta
                                return res.json();
                              }).then(data => {
                                return orderID;
                              });
                            });
                          }}
                          onApprove={(data, actions) => {
                            showNotClosePaypalMessage();
                            // Capture the funds from the transaction
                            return actions.order.capture().then(details => {
                              // Send approved order to the server
                              return fetch(`${Configuration.apiEndPoint}/paypal/order_approved`, {
                                method: 'POST',
                                headers: {
                                  'content-type': 'application/json',
                                  Accept: 'application/json',
                                  Authorization: `Bearer ${token}`
                                },
                                mode: 'cors',
                                body: JSON.stringify({
                                  orderID: data.orderID
                                })
                              }).then(res => {
                                return res.json();
                              }).then(() => {
                                topUpWalletSuccess();
                              });
                            });
                          }}
	                          onShippingChange={(data,actions) => {
	                            return actions.resolve();
	                          }}
	                        />
                            <div className="partner-profile-warning-card">
                              <h4>{t('WARNING')}</h4>
                              <p>{t('ACCOUNT_TOPUP_NOTES')}</p>
                            </div>
                          </div>
	                      </Form>
	                    </Container>
                  );
                }}
              </Formik>
              <AuthMethods token={token}/>
            </Col>
          </Row>
        </Container>
      </Fade>
    </Fragment>
  );
};

PartnerProfile.propTypes = {
  profile: PropTypes.object,
  save: PropTypes.func.isRequired,
  removeFile: PropTypes.func.isRequired,
  token: PropTypes.string,
  topUpWalletSuccess: PropTypes.func.isRequired,
  showNotClosePaypalMessage: PropTypes.func.isRequired,
  deleteRtvChannelImageFile: PropTypes.func.isRequired,
};

PartnerProfile.defaultProps = {
  profile: null,
  token: null
};

export default PartnerProfile;
